import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { roundHalfAwayFromZero } from '@digital-twins/deck-ui/foundations/producer-rounding'
import { useTranslation } from 'react-i18next'
import { MessageSquare, Users, Loader2, AlertCircle, ChevronRight, ChevronLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import { useRouter } from '@/routing/navigation'
import { ComposeModal } from '@/pages/ConversationsPage/ComposeModal/ComposeModal'
import type { TwinSuggestion } from '@/pages/ConversationsPage/ComposeModal/ComposeModal'
import type { SampleDemographics, StudyContext, CanonicalAnalysisOutput, Speaker } from '@digital-twins/shared-types'
import { experimentsApi, chatRoomsApi, populationsApi, publicApi } from '@/lib/api'
import { mergeProfilesWithResponseImages, buildSurveyQuestionContext, buildResponseImageMap } from '@/features/studies/report/audienceProfiles'
import { buildSurveyChoiceLabels } from '@/lib/utils/survey-answer-display'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { GuestTwinChat } from './GuestTwinChat'
import { GuestFocusGroup } from './GuestFocusGroup'
import type { AudienceSummary } from '@/lib/api'
import { getRespondentDescription, getRespondentRole } from './respondentProfileText'
import { ALL_SAVED_SEGMENTS_KEY } from '@/features/studies/report/figures/figureDimensions'
import { combinedSavedSegmentShares } from './sampleProfileSegments'
import { openStudyTwinChat } from '@/pages/ChatPage/openStudyTwinChat'
import { buildStudyCreativeGrounding } from '@/features/studies/report/utils/buildStudyCreativeGrounding'
import { TrialLimitUpgradeModal } from '@/components/billing/TrialLimitUpgradeModal'
import { detectTrialLimitError } from '@/lib/billing/trial-limits'

// ─── Types ────────────────────────────────────────────────────────────────────

interface RespondentProfile {
  respondent_id: string
  chatPersonaToken?: string | null
  chatStudyEvidence?: { answers: Record<string, unknown>; questions: Record<string, string> } | null
  name: string
  image_url: string
  summary: string
  /** The twin's own tagline, stored on the profile: never the role line (D16). */
  tagline?: string
  /** The report's speaker record (`speakers`, D16): its label is the role line. */
  speaker?: Speaker | null
  respondent_attributes: Record<string, any>
  survey_data: Record<string, any>
}

interface ChatTabProps {
  audienceProfiles?: Array<RespondentProfile>
  audienceProfilesLoading?: boolean
  populationName?: string | null
  responses: Array<any>
  populationId?: string
  experimentId?: string
  survey?: unknown
  canonicalAnalysis?: CanonicalAnalysisOutput | null
  /** When set, auto-starts a direct 1:1 chat with the matching twin on mount */
  initialChatTwinName?: string
  onClearInitialTwin?: () => void
  /** When set, enables guest chat mode using public API endpoints */
  shareToken?: string
  experimentLanguage?: string | null
}

// ─── Sample Profile Sidebar ───────────────────────────────────────────────────

const INITIAL_SEGMENTS_SHOWN = 5

const BAR_GRADIENT = 'linear-gradient(to right, hsl(var(--brand-deep, 169 85% 19%)), hsl(var(--brand-accent, 173 86% 40%)))'

function toSentenceCase(str: string): string {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

function shortenDimensionLabel(label: string): string {
  // Strip everything after the first sentence boundary (some labels append long footnotes)
  let s = label.trim().replace(/[?!].*$/, '').trim()

  // Strip question number prefix: S1:, Q3:, S4r98oe:, etc.
  s = s.replace(/^[SsQq]\d+[a-zA-Z]*\s*:\s*/i, '').trim()

  if (s.split(/\s+/).length <= 3) return toSentenceCase(s)

  // Abbreviate verbose known phrases before stripping openers
  s = s
    .replace(/go-to-market\s*\(GTM\)/gi, 'GTM')
    .replace(/go-to-market/gi, 'GTM')
    .replace(/\(GTM\)/gi, 'GTM')

  // Strip verbose question openers — most specific patterns first
  s = s
    .replace(/^which of the following (best\s+)?describes (your\s+)?(company'?s?\s+)?/i, '')
    .replace(/^how do you think (your|the)\s+(company'?s?\s+)?/i, '')
    .replace(/^how (has|have|do|does|did|will) (you|your|the)\s+(company'?s?\s+)?/i, '')
    .replace(/^what (is|are|was|were) (your|the)\s+(company'?s?\s+)?(approximate\s+)?/i, '')
    .replace(/^what is your (company'?s?\s+)?/i, '')
    .replace(/^(please\s+)?(select|choose|indicate|describe|tell us|rate|rank|specify)\s+/i, '')
    .replace(/^(your|the|a|an)\s+/i, '')
    .trim()

  // Strip trailing context noise
  s = s
    .replace(/\s+at (your\s+)?company.*$/i, '')
    .replace(/\s+within (your\s+)?company.*$/i, '')
    .replace(/\s+for (the purposes|the purpose).*$/i, '')
    .replace(/\s+compared to.*$/i, '')
    .replace(/\s+next year.*$/i, '')
    .replace(/\s+last year.*$/i, '')
    .replace(/\s+(will\s+)?change(d)?\s*$/i, '')
    .trim()

  // Remove remaining filler words
  s = s.replace(/\byour\s+/gi, '').replace(/\bcompany'?s?\s+/gi, '').trim()

  return toSentenceCase(s.split(/\s+/).filter(Boolean).slice(0, 4).join(' '))
}

interface ProfileDimension {
  id: string
  label: string
  segments: Array<{ value: string; count: number; percentage: number }>
}

interface DimensionPanelProps {
  dimension: ProfileDimension
  /** Combined-cut heading is the translated "All subsegments" label. Demographic labels are shortened. */
  preserveLabel?: boolean
}

function DimensionPanel(props: DimensionPanelProps) {
  const { dimension, preserveLabel = false } = props

  const { t } = useTranslation('app')
  const [expanded, setExpanded] = useState(false)
  const visibleSegments = expanded ? dimension.segments : dimension.segments.slice(0, INITIAL_SEGMENTS_SHOWN)
  const hasMore = dimension.segments.length > INITIAL_SEGMENTS_SHOWN

  return (
    <div data-testid={preserveLabel ? 'chat-sample-profile-saved-segment' : 'chat-sample-profile-dimension'}>
      <p className="text-xs font-semibold text-foreground/70 mb-2.5">
        {preserveLabel ? dimension.label : shortenDimensionLabel(dimension.label)}
      </p>
      <div className="flex flex-col gap-2">
        {visibleSegments.map((seg) => (
          <div key={seg.value} className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground/80 truncate pr-2">{seg.value}</span>
              <span className="text-xs font-medium text-foreground/70 shrink-0 whitespace-nowrap">
                {roundHalfAwayFromZero(seg.percentage)}%{' '}
                <span className="text-foreground/40">({seg.count})</span>
              </span>
            </div>
            <div className="h-1.5 w-full bg-chart-muted/30 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${seg.percentage}%`, background: BAR_GRADIENT }}
              />
            </div>
          </div>
        ))}
      </div>
      {hasMore && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-3 text-xs font-medium text-foreground/50 hover:text-foreground/80 transition-colors flex items-center gap-1"
        >
          {t('studyReport.loadMore')}
          <span className="text-foreground/30">+</span>
        </button>
      )}
    </div>
  )
}

interface SampleProfileSidebarProps {
  demographics: SampleDemographics | null
  savedSegmentBlock: ProfileDimension | null
  onClose: () => void
}

function SampleProfileSidebar(props: SampleProfileSidebarProps) {
  const { demographics, savedSegmentBlock, onClose } = props

  const { t } = useTranslation('app')

  return (
    <div data-testid="chat-sample-profile-panel" className="h-full w-full">
      <div className="h-full bg-muted/60 rounded-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-4 pb-3 flex items-center justify-between shrink-0">
          <p className="text-sm font-semibold text-foreground">{t('studyReport.sampleProfile')}</p>
          <button
            type="button"
            onClick={onClose}
            data-testid="chat-sample-profile-close"
            className="flex items-center justify-center w-7 h-7 rounded-full bg-background/60 hover:bg-background transition-colors"
          >
            <ChevronRight className="w-3.5 h-3.5 text-foreground/60" />
          </button>
        </div>
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-5 scrollbar-overlay">
          {savedSegmentBlock && (
            <DimensionPanel dimension={savedSegmentBlock} preserveLabel />
          )}
          {(demographics?.dimensions ?? []).map((dim) => (
            <DimensionPanel key={dim.id} dimension={dim} />
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Twin Card ────────────────────────────────────────────────────────────────

const AUDIENCE_DELETED_TOOLTIP_KEY = 'studyReport.audienceDeletedTooltip'

function TwinCard({
  profile,
  onChat,
  isFirst = false,
  isCreating = false,
  audienceDeleted = false,
  displayImageUrl,
}: {
  profile: RespondentProfile
  onChat: (profile: RespondentProfile) => void
  isFirst?: boolean
  isCreating?: boolean
  audienceDeleted?: boolean
  /**
   * The persona's current (enriched) DB image. Preferred over the study-time
   * snapshot in `profile.image_url` so the card matches the image shown in the
   * chat, which is sourced from the same persona record.
   */
  displayImageUrl?: string
}) {
  const { t } = useTranslation(['app', 'common'])
  const role = getRespondentRole(profile)
  const description = getRespondentDescription(profile)
  const imageUrl = displayImageUrl ?? profile.image_url
  const { displayUrl: thumbUrl, handleImageError } = usePersonaThumbFallback(imageUrl, 256)

  const chatButton = (
    <Button
      variant="cta"
      size="sm"
      className="shrink-0 gap-2"
      onClick={() => onChat(profile)}
      disabled={isCreating || audienceDeleted}
      data-testid={`chat-twin-msg-${profile.respondent_id}`}
    >
      {isCreating ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <MessageSquare className="h-4 w-4" />
      )}
      {isCreating ? t('studyReport.opening') : t('studyReport.chatNow')}
    </Button>
  )

  return (
    <div data-testid={`chat-twin-card-${profile.respondent_id}`}>
      {!isFirst && <div className="h-px bg-border" />}
      <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-8 py-6">
        {/* Photo + text */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5 flex-1 min-w-0">
          <div className="w-full sm:w-[284px] sm:max-w-[284px] sm:shrink-0 h-44 rounded-xl bg-muted overflow-hidden flex items-center justify-center">
            {thumbUrl ? (
              <img
                key={thumbUrl}
                src={thumbUrl}
                alt={profile.name}
                loading="lazy"
                className="block w-full h-full object-cover"
                style={{ objectPosition: 'center 10%' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  handleImageError(() => {
                    target.src = '/images/twin-fallback.png'
                  })
                }}
              />
            ) : (
              <Users className="h-7 w-7 text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <p className="font-display font-normal text-xl text-foreground leading-tight">
              {profile.name}
            </p>
            {role && (
              <p className="text-[13px] font-medium text-muted-foreground/80 leading-snug">
                {role}
              </p>
            )}
            {description && (
              <p className="text-muted-foreground text-base leading-[1.6] line-clamp-3 mt-0.5">
                {description}
              </p>
            )}
          </div>
        </div>

        {audienceDeleted ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0">{chatButton}</span>
              </TooltipTrigger>
              <TooltipContent>{t(AUDIENCE_DELETED_TOOLTIP_KEY)}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          chatButton
        )}
      </div>
    </div>
  )
}

// ─── ChatTab ──────────────────────────────────────────────────────────────────

export function ChatTab({
  audienceProfiles: initialProfiles,
  audienceProfilesLoading,
  populationName,
  responses,
  populationId,
  experimentId,
  survey,
  canonicalAnalysis,
  initialChatTwinName,
  onClearInitialTwin,
  shareToken,
  experimentLanguage,
}: ChatTabProps) {
  const { t } = useTranslation('app')
  const router = useRouter()
  const [profiles, setProfiles] = useState<Array<RespondentProfile>>(initialProfiles ?? [])
  const [isLoading, setIsLoading] = useState(!initialProfiles)
  const [error, setError] = useState<string | null>(null)
  const [showSampleProfile, setShowSampleProfile] = useState(false)
  const [composeOpen, setComposeOpen] = useState(false)
  const hasLoaded = useRef(false)
  const [guestChatTwin, setGuestChatTwin] = useState<RespondentProfile | null>(null)
  const [guestFocusRoomToken, setGuestFocusRoomToken] = useState<string | null>(null)
  const isGuestMode = !!shareToken
  // Read inside effects that must not re-run when the token identity changes.
  const [resolvedPersonaMap, setResolvedPersonaMap] = useState<Map<string, string>>(new Map())
  const [resolvedPersonaAttributesMap, setResolvedPersonaAttributesMap] = useState<Map<string, Record<string, unknown>>>(new Map())
  const [resolvedPersonaNormalizedAttrsMap, setResolvedPersonaNormalizedAttrsMap] = useState<Map<string, Record<string, unknown>>>(new Map())
  const [resolvedPersonaTagNamesMap, setResolvedPersonaTagNamesMap] = useState<Map<string, Array<string>>>(new Map())
  const [resolvedPersonaIdentityMap, setResolvedPersonaIdentityMap] = useState<Map<string, TwinSuggestion['resolvedIdentity']>>(new Map())
  const [resolvedPersonaImageMap, setResolvedPersonaImageMap] = useState<Map<string, string>>(new Map())
  const [resolvedPersonaBioMap, setResolvedPersonaBioMap] = useState<Map<string, string>>(new Map())
  const [resolvedPersonaPopulationId, setResolvedPersonaPopulationId] = useState<string | null>(null)
  const [personaMapLoading, setPersonaMapLoading] = useState(true)

  const sampleDemographics: SampleDemographics | null = canonicalAnalysis?.sampleDemographics ?? null
  const savedSegmentShares = useMemo(
    () => combinedSavedSegmentShares(responses),
    [responses],
  )
  const savedSegmentBlock = useMemo((): ProfileDimension | null => {
    if (!savedSegmentShares) {
      return null
    }
    return {
      id: ALL_SAVED_SEGMENTS_KEY,
      label: t('studyReport.questionCard.segmentAllSubsegments'),
      segments: savedSegmentShares,
    }
  }, [savedSegmentShares, t])

  // Display-only question titles for the guest twin chat's answer panel.
  const surveyQuestionsMap = useMemo(
    () => Object.fromEntries(buildSurveyQuestionContext(survey).map((q) => [q.name, q.title])),
    [survey],
  )

  const surveyChoiceLabels = useMemo(
    () => buildSurveyChoiceLabels(survey),
    [survey],
  )

  const creativeGrounding = useMemo(
    () => buildStudyCreativeGrounding(survey),
    [survey],
  )

  const parentOwnsHydration = audienceProfilesLoading !== undefined

  useEffect(() => {
    if (parentOwnsHydration) {
      if (audienceProfilesLoading) {
        setIsLoading(true)
        return
      }
      setProfiles(initialProfiles ?? [])
      setIsLoading(false)
      return
    }

    if (hasLoaded.current) return
    hasLoaded.current = true

    if (initialProfiles && initialProfiles.length > 0) {
      setProfiles(initialProfiles)
      setIsLoading(false)
      return
    }

    if (!experimentId) {
      setIsLoading(false)
      return
    }

    const load = async () => {
      try {
        const cached = await experimentsApi.getAudienceProfiles(experimentId)
        const arr = Array.isArray(cached.profiles)
          ? cached.profiles
          : Array.isArray(cached.profiles?.profiles)
            ? cached.profiles.profiles
            : []
        if (arr.length > 0) {
          setProfiles(mergeProfilesWithResponseImages(arr, responses))
        }
      } catch {
        setError(t('studyReport.loadProfilesFailed'))
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [parentOwnsHydration, audienceProfilesLoading, initialProfiles, experimentId, responses])

  const [creatingChatForId, setCreatingChatForId] = useState<string | null>(null)
  const [trialRoomLimitOpen, setTrialRoomLimitOpen] = useState(false)
  const [initializingGuestChat, setInitializingGuestChat] = useState<string | null>(null)

  const handleGuestChat = useCallback(async (profile: RespondentProfile) => {
    if (!shareToken || initializingGuestChat) return
    setInitializingGuestChat(profile.respondent_id)

    try {
      await publicApi.initChat(shareToken, profile.respondent_id)
      setGuestChatTwin(profile)
    } catch {
      // init failed, still open the chat so user can try sending
      setGuestChatTwin(profile)
    } finally {
      setInitializingGuestChat(null)
    }
  }, [shareToken, initializingGuestChat])

  const handleChat = useCallback(async (profile: RespondentProfile) => {
    if (isGuestMode) {
      handleGuestChat(profile)
      return
    }
    if (!populationId) {
      toast.error(t(AUDIENCE_DELETED_TOOLTIP_KEY))
      return
    }
    if (creatingChatForId) return
    setCreatingChatForId(profile.respondent_id)
    try {
      const resolvedPersonaId = profile.chatPersonaToken
      if (!resolvedPersonaId) {
        toast.error(t('studyReport.twinResolveFailed'))
        setCreatingChatForId(null)
        return
      }

      const resolvedBio = resolvedPersonaBioMap.get(profile.respondent_id)

      await openStudyTwinChat(
        {
          populationId,
          personaId: resolvedPersonaId,
          experimentId,
          experimentLanguage,
          imageUrl: profile.image_url || undefined,
          bio: resolvedBio,
          creatives: creativeGrounding.creatives,
          backHref: window.location.pathname + window.location.search,
        },
        {
          getRoom: (token) => chatRoomsApi.get(token),
          createRoom: (dto) => chatRoomsApi.create(dto),
          navigate: (href, backHref) => {
            onClearInitialTwin?.()
            router.push(href, { state: { backHref } })
          },
        },
      )
    } catch (err) {
      if (detectTrialLimitError(err) === 'rooms') {
        setTrialRoomLimitOpen(true)
      }
      setCreatingChatForId(null)
    }
  }, [isGuestMode, handleGuestChat, populationId, experimentId, creatingChatForId, resolvedPersonaMap, resolvedPersonaPopulationId, resolvedPersonaBioMap, router, onClearInitialTwin, creativeGrounding, experimentLanguage, t])

  // Auto-start direct chat when navigated here from a quote's chat button
  const triggeredTwinRef = useRef<string | null>(null)
  useEffect(() => {
    if (!initialChatTwinName || isLoading || profiles.length === 0) return
    if (triggeredTwinRef.current === initialChatTwinName) return
    const needle = initialChatTwinName.toLowerCase().trim()
    const match =
      profiles.find((p) => (p.name ?? '').toLowerCase().trim() === needle) ??
      profiles.find((p) => (p.name ?? '').toLowerCase().includes(needle)) ??
      profiles.find((p) => needle.includes((p.name ?? '').toLowerCase().trim()))
    if (match) {
      triggeredTwinRef.current = initialChatTwinName
      handleChat(match)
    }
  }, [initialChatTwinName, profiles, isLoading, handleChat])

  const handleFocusGroup = useCallback(() => {
    setComposeOpen(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    setPersonaMapLoading(true)
    setResolvedPersonaPopulationId(null)
    async function resolvePersonaIds() {
      const tokensByRecord = new Map<string, Set<string>>()
      for (const record of [...responses, ...profiles]) {
        if (typeof record.chatPersonaToken !== 'string' || !record.chatPersonaToken) {
          continue
        }
        const tokens = tokensByRecord.get(record.respondent_id) ?? new Set<string>()
        tokens.add(record.chatPersonaToken)
        tokensByRecord.set(record.respondent_id, tokens)
      }
      const idMap = new Map<string, string>()
      const attrsMap = new Map<string, Record<string, unknown>>()
      const normalizedMap = new Map<string, Record<string, unknown>>()
      const tagsMap = new Map<string, Array<string>>()
      const identityMap = new Map<string, TwinSuggestion['resolvedIdentity']>()
      const imageMap = new Map<string, string>()
      const bioMap = new Map<string, string>()
      const tokenIds = [...new Set([...tokensByRecord.values()].filter((ids) => ids.size === 1).map((ids) => [...ids][0]))]
      try {
        if (populationId) {
          const rows = new Map<string, any>()
          // Reuse the authorized full picker projection with literal token selection.
          for (let i = 0; i < tokenIds.length; i += 240) {
            const options = { contextPersonaIds: tokenIds.slice(i, i + 240), limit: 240 }
            const result = shareToken
              ? await publicApi.getPersonasForPicker(shareToken, options)
              : await populationsApi.getPersonasForPicker(populationId, options)
            for (const row of result.personas) {
              rows.set(row.personaId, row)
            }
            if (cancelled) {
              return
            }
          }
          for (const [recordId, tokens] of tokensByRecord) {
            if (tokens.size !== 1) {
              continue
            }
            const token = [...tokens][0]
            const row = rows.get(token)
            if (!row) {
              continue
            }
            idMap.set(recordId, token)
            const display = row.displayAttributes ?? row.attributes
            if (display) {
              attrsMap.set(recordId, display)
            }
            const raw = row.rawAttributes ?? display
            if (raw) {
              normalizedMap.set(recordId, raw)
            }
            if (Array.isArray(row.audienceTagNames)) {
              tagsMap.set(recordId, row.audienceTagNames)
            }
            if (row.resolvedIdentity) {
              identityMap.set(recordId, row.resolvedIdentity)
            }
            if (row.imageUrl) {
              imageMap.set(recordId, row.imageUrl)
            }
            if (row.displayBio) {
              bioMap.set(recordId, row.displayBio)
            }
          }
        }
      } catch {
        // Failed authorized reads leave the source unavailable.
      }
      if (cancelled) {
        return
      }
      setResolvedPersonaMap(idMap)
      setResolvedPersonaAttributesMap(attrsMap)
      setResolvedPersonaNormalizedAttrsMap(normalizedMap)
      setResolvedPersonaTagNamesMap(tagsMap)
      setResolvedPersonaIdentityMap(identityMap)
      setResolvedPersonaImageMap(imageMap)
      setResolvedPersonaBioMap(bioMap)
      setResolvedPersonaPopulationId(populationId ?? null)
      setPersonaMapLoading(false)
    }
    void resolvePersonaIds()
    return () => { cancelled = true }
  }, [populationId, profiles, responses, shareToken])

  // Pre-map profiles for ComposeModal focus group twin picker.
  // Uses all responses (not just the 10 featured profiles) so every study
  // respondent is selectable in a focus group, merging images from profiles,
  // enriched persona DB rows, and response-stamped URLs when available.
  const studyRespondentsForCompose = useMemo((): Array<TwinSuggestion> | null => {
    if (responses.length === 0 || personaMapLoading || resolvedPersonaPopulationId !== populationId) return null

    const profileImageMap = new Map<string, string>()
    for (const p of profiles) {
      if (p.image_url) profileImageMap.set(p.respondent_id, p.image_url)
    }
    const responseImageMap = buildResponseImageMap(responses)

    const seen = new Set<string>()
    const result: Array<TwinSuggestion> = []
    for (const response of responses) {
      const name = String(response.persona_name || response.respondent_id || '').trim()
      if (!name) continue
      const key = response.respondent_id
      if (seen.has(key)) continue
      const personaId = resolvedPersonaMap.get(key)
      if (!personaId) continue
      seen.add(key)
      const imageUrl =
        profileImageMap.get(key)
        ?? resolvedPersonaImageMap.get(key)
        ?? responseImageMap.get(key)
        ?? null
      // Prefer the clean persona attributes from the DB (via picker search response)
      // over respondent_attributes which contains debug/runtime payload
      const personaAttrs = resolvedPersonaAttributesMap.get(key)
      const attributes = personaAttrs ?? (response.respondent_attributes ?? {}) as Record<string, unknown>
      const rawAttributes = resolvedPersonaNormalizedAttrsMap.get(key)
      const audienceTagNames = resolvedPersonaTagNamesMap.get(key)
      const resolvedIdentity = resolvedPersonaIdentityMap.get(key) ?? null
      const bio = resolvedPersonaBioMap.get(key) ?? null
      result.push({
        personaId,
        personaName: name,
        imageUrl,
        bio,
        isReady: !!imageUrl,
        attributes,
        rawAttributes,
        audienceTagNames,
        resolvedIdentity,
      })
    }
    return result.length > 0 ? result : null
  }, [responses, profiles, resolvedPersonaMap, resolvedPersonaAttributesMap, resolvedPersonaNormalizedAttrsMap, resolvedPersonaTagNamesMap, resolvedPersonaIdentityMap, resolvedPersonaImageMap, resolvedPersonaBioMap, resolvedPersonaPopulationId, populationId, personaMapLoading])

  const focusGroupStudyContext = useMemo((): StudyContext | undefined => {
    if (!experimentId || responses.length === 0 || personaMapLoading || resolvedPersonaPopulationId !== populationId) return undefined
    return { experimentId }
  }, [experimentId, responses, resolvedPersonaMap, resolvedPersonaPopulationId, populationId, personaMapLoading, creativeGrounding])

  // Guests can't list audiences, so the composer's audience is described from
  // what the public report already carries.
  const guestAudienceSnapshot = useMemo((): AudienceSummary | null => {
    if (!isGuestMode || !populationId) return null
    return {
      id: populationId,
      name: populationName || 'Study audience',
      status: 'ready',
      rowCount: sampleDemographics?.sampleSize ?? responses.length,
    } as AudienceSummary
  }, [isGuestMode, populationId, populationName, sampleDemographics, responses.length])

  const hasDemographics = !!sampleDemographics && sampleDemographics.dimensions.length > 0
  const hasSampleProfile = hasDemographics || savedSegmentBlock !== null
  const audienceTypeLabel = sampleDemographics?.audienceType
    ? ` (${sampleDemographics.audienceType.toUpperCase()})`
    : ''

  if (isGuestMode && guestFocusRoomToken && shareToken) {
    return (
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden font-sans">
        <GuestFocusGroup
          shareToken={shareToken}
          roomToken={guestFocusRoomToken}
          onBack={() => setGuestFocusRoomToken(null)}
        />
      </div>
    )
  }

  if (isGuestMode && guestChatTwin && shareToken) {
    return (
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden font-sans">
        <GuestTwinChat
          shareToken={shareToken}
          participantId={guestChatTwin.respondent_id}
          profile={guestChatTwin}
          surveyQuestionsMap={surveyQuestionsMap}
          surveyChoiceLabels={surveyChoiceLabels}
          onBack={() => setGuestChatTwin(null)}
        />
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden font-sans">
      {/* Secondary header */}
      <div className="shrink-0 flex items-center justify-between py-4">
        <div className="flex flex-col gap-0.5">
          <h2 className="font-display font-normal text-2xl text-foreground leading-tight">
            {populationName ? `${populationName}${audienceTypeLabel}` : t('studyReport.studyAudience')}
          </h2>
          <p className="font-sans text-sm text-muted-foreground">
            {t('studyLobby.respondents', { count: sampleDemographics?.sampleSize ?? responses.length })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(() => {
            const audienceDeleted = !populationId
            const btn = (
              <button
                type="button"
                onClick={audienceDeleted ? undefined : handleFocusGroup}
                disabled={audienceDeleted}
                data-testid="chat-start-focus-group"
                className="font-sans px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('studyReport.startFocusGroup')}
              </button>
            )
            return audienceDeleted ? (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>{btn}</span>
                  </TooltipTrigger>
                  <TooltipContent>{t(AUDIENCE_DELETED_TOOLTIP_KEY)}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : btn
          })()}
          {hasSampleProfile && !showSampleProfile && (
            <button
              type="button"
              onClick={() => setShowSampleProfile(true)}
              data-testid="chat-sample-profile-toggle"
              className="font-sans flex items-center gap-2.5 text-sm font-medium text-foreground hover:opacity-70 transition-opacity"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                <ChevronLeft className="w-4 h-4 text-foreground" />
              </span>
              {t('studyReport.sampleProfile')}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden gap-4">
        {/* Twin list */}
        <div className="flex-1 overflow-y-auto scrollbar-overlay">
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-16"
              >
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-3" />
                <p className="text-sm text-muted-foreground">{t('studyReport.loadingProfiles')}</p>
              </motion.div>
            ) : error ? (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-16"
              >
                <AlertCircle className="w-8 h-8 text-destructive mb-3" />
                <p className="text-sm text-muted-foreground">{error}</p>
              </motion.div>
            ) : profiles.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-16"
              >
                <Users className="w-8 h-8 text-muted-foreground mb-3" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">{t('studyReport.noProfiles')}</p>
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {profiles.map((profile, idx) => (
                  <TwinCard
                    key={profile.respondent_id}
                    profile={profile}
                    onChat={handleChat}
                    isFirst={idx === 0}
                    isCreating={creatingChatForId === profile.respondent_id || initializingGuestChat === profile.respondent_id}
                    audienceDeleted={!populationId}
                    displayImageUrl={
                      resolvedPersonaPopulationId === populationId
                        ? resolvedPersonaImageMap.get(profile.respondent_id)
                        : undefined
                    }
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sample profile sidebar */}
        <AnimatePresence>
          {showSampleProfile && hasSampleProfile && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="overflow-hidden shrink-0 h-full"
            >
              <SampleProfileSidebar
                demographics={sampleDemographics}
                savedSegmentBlock={savedSegmentBlock}
                onClose={() => setShowSampleProfile(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ComposeModal — focus group only */}
      {populationId && (
        <ComposeModal
          open={composeOpen}
          onOpenChange={setComposeOpen}
          groupMode
          lockedAudienceId={populationId}
          // Guest rooms get their study grounding server-side (the guest never
          // sees other respondents' answers), so no studyContext is sent.
          studyContext={isGuestMode ? undefined : focusGroupStudyContext}
          stimulusImagePolicy={
            creativeGrounding.creatives.length > 0 || !!experimentId
              ? 'always'
              : null
          }
          studyRespondents={studyRespondentsForCompose}
          initialLanguage={experimentLanguage}
          {...(isGuestMode && shareToken
            ? {
                guestScope: { shareToken },
                initialAudienceSnapshot: guestAudienceSnapshot,
                onRoomCreated: (roomToken: string) => {
                  setComposeOpen(false)
                  setGuestFocusRoomToken(roomToken)
                },
              }
            : {})}
        />
      )}
      <TrialLimitUpgradeModal
        open={trialRoomLimitOpen}
        onClose={() => setTrialRoomLimitOpen(false)}
        kind="rooms"
      />
    </div>
  )
}
